import { Component, HostListener, OnInit } from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { InputOtp } from 'primeng/inputotp';
import { TagModule } from 'primeng/tag';
import Swal from 'sweetalert2';
import { MfaService } from 'src/shared/services/mfa.service';
import { Unsubscriber } from 'src/shared/components/unsubscriber/unsubscriber.component';

type MfaDialog = 'none' | 'setup' | 'regenerate' | 'disable';
type MfaStep = 'form' | 'codes';

@Component({
  selector: 'app-mfa-settings',
  templateUrl: './mfa-settings.component.html',
  styleUrls: ['./mfa-settings.component.less'],
  imports: [ReactiveFormsModule, TranslateModule, InputOtp, ButtonModule, TagModule],
})
export class MfaSettingsComponent extends Unsubscriber implements OnInit {
  /** null = carregando; true/false = status do 2FA do usuário logado */
  public mfaEnabled: boolean | null = null;
  public statusError = false;

  /** Diálogo aberto e etapa dentro dele */
  public dialog: MfaDialog = 'none';
  public step: MfaStep = 'form';

  public loading = false;
  /** Chave de tradução do erro exibido no diálogo */
  public errorKey: string | null = null;

  /** QR code do cadastro (data:image/png;base64,...) */
  public qrCode = '';
  /** Códigos de recuperação em claro: existem só até o diálogo fechar */
  public recoveryCodes: string[] = [];
  public codesSaved = false;
  public copied = false;

  public codeControl = new FormControl('', [
    Validators.required,
    Validators.pattern(/^\d{6}$/),
  ]);
  public passwordControl = new FormControl('', Validators.required);

  constructor(
    private mfaService: MfaService,
    private translate: TranslateService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.loadStatus();
  }

  /** Habilita o botão de confirmar do diálogo atual */
  get canSubmit(): boolean {
    if (this.loading || this.codeControl.invalid) {
      return false;
    }
    if (this.dialog === 'setup' && !this.qrCode) {
      return false;
    }
    if (this.dialog === 'disable' && this.passwordControl.invalid) {
      return false;
    }
    return true;
  }

  /** O diálogo dos códigos só fecha depois de o usuário confirmar que salvou */
  get canClose(): boolean {
    return !this.loading && (this.step !== 'codes' || this.codesSaved);
  }

  private loadStatus(): void {
    this.statusError = false;
    this.addSubscription(
      this.mfaService.getStatus(localStorage.getItem('username')).subscribe({
        next: (enabled) => {
          this.mfaEnabled = enabled;
        },
        error: () => {
          this.statusError = true;
        },
      }),
    );
  }

  // ---------- Abrir diálogos ----------

  public openSetup(): void {
    this.resetDialogState();
    this.dialog = 'setup';
    this.loading = true;
    this.addSubscription(
      this.mfaService.setupStart().subscribe({
        next: (qrCode) => {
          this.qrCode = qrCode;
          this.loading = false;
        },
        error: (error) => {
          this.errorKey = this.mfaService.errorKey(error);
          this.loading = false;
        },
      }),
    );
  }

  public openRegenerate(): void {
    this.resetDialogState();
    this.dialog = 'regenerate';
  }

  public openDisable(): void {
    this.resetDialogState();
    this.dialog = 'disable';
  }

  // ---------- Confirmar ----------

  public submit(): void {
    if (!this.canSubmit) {
      return;
    }
    if (this.dialog === 'setup') {
      this.confirmSetup();
    } else if (this.dialog === 'regenerate') {
      this.confirmRegenerate();
    } else if (this.dialog === 'disable') {
      this.confirmDisable();
    }
  }

  private confirmSetup(): void {
    this.loading = true;
    this.errorKey = null;
    this.addSubscription(
      this.mfaService.setupConfirm(this.codeControl.value!).subscribe({
        next: (codes) => {
          this.recoveryCodes = codes;
          this.mfaEnabled = true;
          this.showCodes();
        },
        error: (error) => this.onError(error),
      }),
    );
  }

  private confirmRegenerate(): void {
    this.loading = true;
    this.errorKey = null;
    this.addSubscription(
      this.mfaService
        .regenerateRecoveryCodes(this.codeControl.value!)
        .subscribe({
          next: (codes) => {
            this.recoveryCodes = codes;
            this.showCodes();
          },
          error: (error) => this.onError(error),
        }),
    );
  }

  private confirmDisable(): void {
    this.loading = true;
    this.errorKey = null;
    this.addSubscription(
      this.mfaService
        .disable(this.passwordControl.value!, this.codeControl.value!)
        .subscribe({
          next: (enabled) => {
            this.mfaEnabled = enabled;
            this.loading = false;
            this.closeDialog();
            Swal.fire({
              icon: 'success',
              title: this.translate.instant('mfa.disabled-success'),
              timer: 3000,
              showConfirmButton: false,
            });
          },
          error: (error) => this.onError(error),
        }),
    );
  }

  private showCodes(): void {
    this.loading = false;
    this.codeControl.reset('');
    this.step = 'codes';
  }

  private onError(error: any): void {
    this.loading = false;
    this.errorKey = this.mfaService.errorKey(error);
  }

  // ---------- Códigos de recuperação ----------

  public copyCodes(): void {
    navigator.clipboard
      .writeText(this.recoveryCodes.join('\n'))
      .then(() => {
        this.copied = true;
      })
      .catch(() => {
        this.copied = false;
      });
  }

  public downloadCodes(): void {
    const header = this.translate.instant('mfa.file-header');
    const content = `${header}\n\n${this.recoveryCodes.join('\n')}\n`;
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'smartgreen-2fa-codigos-recuperacao.txt';
    link.click();
    URL.revokeObjectURL(url);
  }

  // ---------- Campos e fechamento ----------

  @HostListener('document:keydown.escape')
  public onEscape(): void {
    this.closeDialog();
  }

  public closeDialog(): void {
    if (this.dialog === 'none' || !this.canClose) {
      return;
    }
    this.resetDialogState();
    this.dialog = 'none';
  }

  /** Descarta QR, códigos e campos: nada disso deve sobrar na memória do componente */
  private resetDialogState(): void {
    this.step = 'form';
    this.loading = false;
    this.errorKey = null;
    this.qrCode = '';
    this.recoveryCodes = [];
    this.codesSaved = false;
    this.copied = false;
    this.codeControl.reset('');
    this.passwordControl.reset('');
  }
}
